import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'danger'
  | 'dangerGhost'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'chrome' | 'lg' | 'icon' | 'iconSm';

// Der Primärknopf trägt ein Helligkeitsgefälle von rund 5 % plus eine helle
// Innenkante oben (`shadow-btn`). Beides zusammen macht ihn körperlich; der
// Verlauf allein sähe nur bunt aus. Beim Drücken kehrt sich die Kante nach
// innen – dieselbe Bewegung, die eine echte Taste macht.
const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-linear-to-b from-brand to-brand-hover text-brand-ink shadow-btn',
    'hover:from-brand-hover hover:to-brand-active',
    'active:from-brand-active active:to-brand-active active:shadow-btn-active',
  ),
  secondary:
    'bg-elevated text-ink border border-line shadow-xs hover:bg-sunken hover:border-line-strong',
  outline: 'border border-line-strong text-ink hover:bg-sunken',
  ghost: 'text-ink-2 hover:bg-sunken hover:text-ink',
  danger: 'bg-danger text-white shadow-btn hover:brightness-110 active:brightness-95 active:shadow-btn-active',
  dangerGhost: 'text-danger hover:bg-danger-soft',
  link: 'text-accent-strong underline-offset-4 hover:underline',
};

/**
 * Die Höhenstufung der Oberfläche – hier, weil der Knopf sie am häufigsten
 * trägt. Eingabefelder (`field.tsx`), Filterpillen (`paper.tsx`) und der
 * segmentierte Umschalter (`misc.tsx`) richten sich an denselben Zahlen aus;
 * wer eine davon ändert, ändert alle vier.
 *
 *   Stufe    mobil   Desktop   Schrift          wo
 *   ─────────────────────────────────────────────────────────────────────
 *   lg        48        44      16 / 15 px      Anmeldung, große Aktion
 *   chrome    44        40      15 / 14 px      Kopfzeile der App
 *   md        44        38      15 / 14 px      Vorgabe: Formulare, Dialoge
 *   sm        36        34      14 / 13 px      in Karten, Listenzeilen
 *
 * Mobil zuerst: mit dem Finger will man gut 44px treffen, mit der Maus
 * reichen 34–40px und die Oberfläche bleibt dichter. Die Schrift wächst mit,
 * sonst wirkt der Knopf innen hohl.
 *
 * 38px für `md` und 34px für `sm` sind keine runden Zahlen aus Laune: sie
 * treffen genau die Filterpille (34) und das Eingabefeld (38), damit in einer
 * Filterzeile nichts um zwei Pixel gegeneinander versetzt steht. Vorher waren
 * es 36 und 32 – und jede Zeile, in der Pille und Knopf nebeneinander lagen,
 * war schief.
 *
 * Die Innenabstände sind gegenüber einem eckigen Knopf um eine halbe Stufe
 * gewachsen: an einer vollen Rundung sitzt Text sonst optisch am Rand, auch
 * wenn er metrisch dieselben Pixel Abstand hat.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-sm sm:h-8.5 sm:px-3 sm:text-[13px] gap-1.5',
  md: 'h-11 px-5 text-[15px] sm:h-9.5 sm:px-4 sm:text-sm gap-2',
  chrome: 'h-11 px-4.5 text-[15px] sm:h-10 sm:px-4 sm:text-sm gap-2',
  lg: 'h-12 px-6 text-base sm:h-11 sm:text-[15px] gap-2',
  icon: 'size-11 sm:size-9.5 shrink-0',
  iconSm: 'size-9 sm:size-8.5 shrink-0',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading, asChild, children, disabled, type, ...props },
  ref,
) {
  const Component = asChild ? Slot : 'button';
  return (
    <Component
      ref={ref}
      // Ohne ausdrückliches `type` ist ein <button> im Formular ein
      // Absenden-Knopf. Ein „Datei hochladen“-Knopf mitten in einem Dialog
      // schickt dann still das Formular ab und schließt es – deshalb ist
      // `button` die Vorgabe und `submit` die Ausnahme, die man hinschreibt.
      // Bei `asChild` entscheidet das Kind (oft ein <a>, dort wäre `type`
      // ungültiges HTML).
      {...(asChild ? {} : { type: type ?? 'button' })}
      disabled={disabled || loading}
      className={cn(
        'relative inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap',
        'transition-[background-color,border-color,color,box-shadow] duration-150',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden />
          <span className="sr-only">Wird ausgeführt…</span>
          {size !== 'icon' && size !== 'iconSm' ? children : null}
        </>
      ) : (
        children
      )}
    </Component>
  );
});
